import './SportsSelector.less';
import '../../../common/less/sports.less';

import clsx from 'clsx';
import i18n from '../../../common/i18n';
import React, { memo, useEffect, useState } from 'react';
import AllOptionsIcon from '../../../icons/svg/all-options-icon-sv.svg';
import { SportIconNui } from '../../../icons/SportIconNui';

/**
 * @param twoColumns defines two-column or four-column <SportSelector /> layout
 * @param onSelectionChange defines action that fires if user clicks on <SportTag />
 * @param selectedSportIds defines array of selected sports
 * @param sportIds defines array of all available sports for <SportsSelector />
 * @param permanentlyVisibleSports defines number of permanent displayed sport tags (if none from rareSports is selected)
 */

const SportsSelector = memo(
    ({ twoColumns, onSelectionChange, permanentlyVisibleSports, selectedSportIds, sportIds }) => {
        const showToggleButton = permanentlyVisibleSports < sportIds.length;
        const favouriteSports = sportIds.slice(0, permanentlyVisibleSports);
        const rareSports = sportIds.slice(permanentlyVisibleSports, sportIds.length);
        const isRareSportActive = selectedSportIds?.length
            ? rareSports.some(sport => selectedSportIds.includes(sport))
            : false;
        const [expanded, setExpanded] = useState(isRareSportActive);
        const [sportsToDisplay, setSportsToDisplay] = useState([]);

        useEffect(() => {
            if (sportIds) {
                setExpanded(expanded);
                setSportsToDisplay(isRareSportActive || expanded ? sportIds : favouriteSports);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [sportIds, isRareSportActive]);

        const toggleButtonTitle =
            expanded || (isRareSportActive && !expanded)
                ? i18n('nui.sports-selector.show-less')
                : i18n('nui.sports-selector.show-more');
        const isFavouriteSports = sportsToDisplay.length === permanentlyVisibleSports;

        function onClick(e) {
            const sportId = parseInt(e.currentTarget.value);
            let newSportIds = [];
            if (sportId) {
                if (selectedSportIds.find(item => item === sportId)) {
                    newSportIds = selectedSportIds.filter(item => item !== sportId);
                } else {
                    newSportIds = [...selectedSportIds, sportId].sort();
                }
            }
            onSelectionChange(newSportIds);
        }
        function onToggleButtonClick() {
            if (isFavouriteSports) {
                setExpanded(true);
                setSportsToDisplay(sportIds);
            } else {
                setExpanded(false);
                setSportsToDisplay(favouriteSports);
            }
        }

        return (
            <div className="sports-selector-wrapper">
                <div className="sports-selector-container flex flex-wrap">
                    <button
                        className={clsx('sport-tag br-default br-radius-xs c-default-light fs-14 pointer', {
                            selected: !selectedSportIds.length,
                            'two-columns': twoColumns,
                            'four-columns': !twoColumns
                        })}
                        onClick={onClick}
                    >
                        <AllOptionsIcon />
                        {i18n('nui.sports-selector.all')}
                    </button>
                    {sportsToDisplay.length
                        ? sportsToDisplay.map(sportId => (
                              <SportTag
                                  key={sportId}
                                  selected={selectedSportIds.includes(sportId)}
                                  sportId={sportId}
                                  onClick={onClick}
                                  twoColumns={twoColumns}
                              />
                          ))
                        : null}
                </div>
                {showToggleButton ? (
                    <button
                        className="sports-toggle-button nui-blue-link pointer fs-16 mt-20"
                        onClick={onToggleButtonClick}
                        data-atid="betbook-sports-selector-toggle-btn"
                    >
                        {toggleButtonTitle}
                    </button>
                ) : null}
            </div>
        );
    }
);

const SportTag = ({ selected, sportId, twoColumns, onClick }) => {
    return (
        <button
            className={clsx('sport-tag br-default br-radius-xs c-default-light fs-14 pointer', {
                selected,
                'two-columns': twoColumns,
                'four-columns': !twoColumns
            })}
            data-atid={`betbook-sport-tag-${sportId}`}
            onClick={onClick}
            value={sportId}
        >
            <SportIconNui
                sportId={sportId}
                className={clsx(`mr-5`, {
                    'icon-white': selected
                })}
            />
            {i18n(`nui.sport.${sportId}`)}
        </button>
    );
};

export default SportsSelector;
